import { useState, useMemo } from 'react';
import { DayPicker } from 'react-day-picker';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format,
  isSameDay,
  /*startOfMonth, endOfMonth, eachDayOfInterval, */ isWithinInterval,
} from 'date-fns';
import { SocialPost } from './ProjectDetail';
import { cn } from '../ui/utils';

interface TimelineCalendarProps {
  posts: SocialPost[];
  projectName?: string;
}

const getPlatformColor = (platform: string): string => {
  switch (platform.toLowerCase()) {
    case 'twitter':
    case 'x':
      return '#000000'; // X/Twitter is black
    case 'linkedin':
      return '#2563eb'; // LinkedIn blue
    case 'instagram':
      return '#E4405F'; // Instagram pink (using solid color for dot)
    case 'facebook':
      return '#1877F2'; // Facebook blue
    case 'youtube':
      return '#FF0000'; // YouTube red
    case 'threads':
      return '#000000'; // Threads is black
    case 'tiktok':
      return '#000000'; // TikTok is black
    case 'bluesky':
      return '#00A8FF'; // BlueSky sky blue
    case 'snapchat':
      return '#FFFC00'; // Snapchat yellow
    case 'pinterest':
      return '#BD081C'; // Pinterest red
    default:
      return '#6b7280'; // Gray
  }
};

const getStatusColor = (status: SocialPost['status']): string | null => {
  switch (status) {
    case 'published':
      return '#10b981'; // Green
    case 'scheduled':
      return null; // Use platform color
    case 'in-review':
      return '#f59e0b'; // Yellow/Amber
    case 'approved':
      return '#3b82f6'; // Blue
    case 'draft':
    default:
      return '#9ca3af'; // Gray
  }
};

export function TimelineCalendar({ posts, projectName }: TimelineCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Calculate project date range (first scheduled post to last scheduled post)
  const projectDateRange = useMemo(() => {
    const scheduledPosts = posts.filter((post) => post.scheduledDate);
    if (scheduledPosts.length === 0) return null;

    const dates = scheduledPosts
      .map((post) => new Date(post.scheduledDate!))
      .sort((a, b) => a.getTime() - b.getTime());

    return {
      start: dates[0],
      end: dates[dates.length - 1],
    };
  }, [posts]);

  // Group posts by date and extract platforms
  const postsByDate = useMemo(() => {
    const grouped: Record<string, Array<{ post: SocialPost; platform: string }>> = {};
    posts.forEach((post) => {
      const dateStr = post.scheduledDate || post.dueDate;
      if (dateStr) {
        const date = new Date(dateStr);
        const dateKey = format(date, 'yyyy-MM-dd');

        // Get platforms from targets or fallback to platforms array
        const platforms =
          post.targets && post.targets.length > 0
            ? post.targets.map((target: any) => target.platform)
            : post.platforms || [];

        platforms.forEach((platform: string) => {
          if (!grouped[dateKey]) {
            grouped[dateKey] = [];
          }
          grouped[dateKey].push({ post, platform });
        });
      }
    });
    return grouped;
  }, [posts]);

  const getPostsForDate = (date: Date): Array<{ post: SocialPost; platform: string }> => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return postsByDate[dateKey] || [];
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const monthName = format(currentDate, 'MMMM yyyy');

  // Get all days in the current month view
  //const monthStart = startOfMonth(currentDate);
  //const monthEnd = endOfMonth(currentDate);
  //const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Calculate if a day is within the project range
  const isDayInProjectRange = (date: Date): boolean => {
    if (!projectDateRange) return false;
    return isWithinInterval(date, { start: projectDateRange.start, end: projectDateRange.end });
  };

  // Custom Day component to render posts
  const CustomDay = (props: any) => {
    const dayObj = props.day;
    const date = dayObj?.date;
    const outside = dayObj?.outside || props.modifiers?.outside || false;
    const className = props.className || '';

    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return (
        <div
          className={cn(
            'relative min-h-28 p-1 border-r-[0.5px] border-b-[0.5px] border-gray-200 opacity-50',
            className
          )}
        />
      );
    }

    const isCurrentMonth = !outside;
    const postsOnDay = isCurrentMonth ? getPostsForDate(date) : [];
    const dayNumber = format(date, 'd');
    const isToday = isSameDay(date, new Date());
    const inProjectRange = isDayInProjectRange(date);
    const isFirstDay = projectDateRange && isSameDay(date, projectDateRange.start);
    const isLastDay = projectDateRange && isSameDay(date, projectDateRange.end);

    return (
      <button
        type="button"
        className={cn(
          'relative min-h-28 p-1 border-r-[0.5px] border-b-[0.5px] border-gray-200 text-left w-full h-full flex flex-col',
          'hover:bg-cmp-bronze-soft/20 transition-colors',
          !isCurrentMonth && 'opacity-50',
          className
        )}
        style={{
          backgroundColor: outside ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
        }}
        aria-label={format(date, 'PPP')}
      >
        {/* Day number */}
        <div
          className="absolute inline-flex items-center justify-center"
          style={{
            color: isToday ? '#ffffff' : '#282b35',
            fontWeight: 'bold',
            fontSize: '12px',
            display: 'inline-flex',
            visibility: 'visible',
            width: isToday ? '24px' : 'auto',
            height: isToday ? '24px' : 'auto',
            minWidth: isToday ? '24px' : 'auto',
            minHeight: isToday ? '24px' : 'auto',
            borderRadius: isToday ? '50%' : '0',
            backgroundColor: isToday ? '#2babaf' : 'transparent',
            top: '2px',
            right: '4px',
            zIndex: 10,
          }}
        >
          {dayNumber}
        </div>

        {/* Project timeline bar */}
        {inProjectRange && isCurrentMonth && (
          <div
            className="absolute bg-gray-400 flex items-center"
            style={{
              top: '32px',
              height: '20px',
              zIndex: 1,
              left: isFirstDay ? '0' : '-1px',
              right: isLastDay ? '0' : '-1px',
            }}
          >
            {isFirstDay && projectName && (
              <span className="text-white text-xs font-medium px-2 truncate">{projectName}</span>
            )}
          </div>
        )}

        {/* Platform dots */}
        {isCurrentMonth && postsOnDay.length > 0 && (
          <div className="mt-14 flex flex-wrap gap-1 pt-1">
            {postsOnDay.map((item, index) => {
              const platformColor = getPlatformColor(item.platform);
              const statusColor = getStatusColor(item.post.status);
              // Use status color for fill if not scheduled, otherwise use platform color
              const fillColor = statusColor || platformColor;
              // Always use platform color for border
              const borderColor = platformColor;

              return (
                <div
                  key={`${item.post.id}-${item.platform}-${index}`}
                  className="w-3 h-3 rounded-full border-2 shadow-sm"
                  style={{
                    backgroundColor: fillColor,
                    borderColor: borderColor,
                  }}
                  title={`${item.post.title || ''} - ${item.platform} (${item.post.status})`}
                />
              );
            })}
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <div
        className="rounded-lg border border-gray-200 overflow-hidden"
        style={{ backgroundColor: '#ffffff' }}
      >
        <style>{`
          [class*="rdp"] table,
          [class*="rdp"] table thead,
          [class*="rdp"] table tbody,
          [class*="rdp"] table tr {
            display: block !important;
          }
          [class*="rdp"] table thead tr,
          [class*="rdp"] table tbody tr {
            display: flex !important;
            width: 100% !important;
          }
          [class*="rdp"] table thead th,
          [class*="rdp"] table tbody td {
            display: block !important;
            flex: 1 1 0% !important;
            min-width: 0 !important;
            width: calc(100% / 7) !important;
            max-width: calc(100% / 7) !important;
            border-width: 0.5px !important;
            border-top-width: 0.5px !important;
            border-bottom-width: 0.5px !important;
            border-left-width: 0.5px !important;
            border-right-width: 0.5px !important;
          }
          .rdp-month_caption {
            display: none !important;
          }
          [class*="rdp"] table thead th {
            color: #282b35 !important;
          }
        `}</style>
        {/* Custom header with arrows and month */}
        <div className="flex justify-center items-center gap-2 pt-1 w-full mb-6 px-4">
          <button
            type="button"
            onClick={handlePreviousMonth}
            className={cn(
              'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 rounded border border-gray-300 hover:bg-gray-100 flex items-center justify-center'
            )}
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4" style={{ color: '#282b35' }} />
          </button>
          <h2 className="text-lg font-medium" style={{ color: '#282b35' }}>
            {monthName}
          </h2>
          <button
            type="button"
            onClick={handleNextMonth}
            className={cn(
              'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 rounded border border-gray-300 hover:bg-gray-100 flex items-center justify-center'
            )}
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4" style={{ color: '#282b35' }} />
          </button>
        </div>
        <DayPicker
          mode="single"
          month={currentDate}
          onMonthChange={setCurrentDate}
          className="p-0"
          classNames={{
            months: 'flex flex-col',
            month: 'flex flex-col gap-0 w-full -mt-2',
            caption: 'hidden',
            caption_label: '',
            nav: 'hidden',
            nav_button: '',
            nav_button_previous: '',
            nav_button_next: '',
            table: 'w-full border-separate border-spacing-0',
            head_row: 'flex border-b-[0.5px] border-gray-200',
            head_cell:
              'font-medium text-sm rounded-md font-normal text-[0.8rem] bg-blue-600 p-2 border-r-[0.5px] border-gray-200 last:border-r-0 flex-1 border-t-0 border-l-0 border-b-0',
            row: 'flex w-full',
            cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 border-r-[0.5px] border-b-[0.5px] border-gray-200 border-t-0 border-l-0 min-h-28 flex-1',
            day: 'h-full p-0 w-full',
            day_selected:
              'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
            day_today: 'bg-accent',
            day_outside: 'day-outside',
            day_disabled: 'text-muted-foreground opacity-50',
            day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
            day_hidden: 'invisible',
          }}
          style={
            {
              '--rdp-cell-width': 'calc(100% / 7)',
            } as React.CSSProperties
          }
          showOutsideDays={true}
          fixedWeeks={true}
          components={{
            Day: CustomDay,
          }}
        />
      </div>

      {/* Legend */}
      <div className="flex gap-4 items-center justify-center flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-1 bg-gray-400"></div>
          <span className="text-sm">Project Timeline</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full border-2 border-gray-300"
            style={{ backgroundColor: '#1877F2' }}
          ></div>
          <span className="text-sm">Facebook</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full border-2 border-gray-300"
            style={{ backgroundColor: '#E4405F' }}
          ></div>
          <span className="text-sm">Instagram</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full border-2 border-gray-300"
            style={{ backgroundColor: '#2563eb' }}
          ></div>
          <span className="text-sm">LinkedIn</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full border-2 border-gray-300"
            style={{ backgroundColor: '#000000' }}
          ></div>
          <span className="text-sm">X/Twitter</span>
        </div>
      </div>
    </div>
  );
}
